import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type Context = 'gallery';

const MAX_TAGS = 8;

const hashtagSuggestions: Record<Context, string[]> = {
  gallery: [
    '#travel', '#sunset', '#architecture', '#nature',
    '#photooftheday', '#landscape', '#urban', '#clouds',
    '#sky', '#explore', '#buildings', '#art', '#cityscape',
  ],
};

const getHashtagSuggestions = (context: Context): string[] => {
  return hashtagSuggestions[context] || [];
};

interface Props {
  context: Context;
  onChange: (tags: string[]) => void;
  selectedTags: string[];
}

const HashtagSelector: React.FC<Props> = ({ context, onChange, selectedTags }) => {
  const { t } = useTranslation();
  const suggestions = getHashtagSuggestions(context);
  const [inputTag, setInputTag] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isFull = selectedTags.length >= MAX_TAGS;

  const handleAddTag = (tag: string) => {
    const raw = tag.trim();
    if (!raw) return;

    const cleaned = raw.startsWith('#') ? raw : `#${raw}`;
    if (selectedTags.includes(cleaned)) {
      setInputTag('');
      setShowDropdown(false);
      return;
    }

    if (isFull) {
      setInputTag('');
      setShowDropdown(false);
      return;
    }

    onChange([...selectedTags, cleaned]);
    setInputTag('');
    setShowDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputTag.trim() !== '') {
        handleAddTag(inputTag);
      }
    }
  };

  const handleRemoveTag = (tag: string) => {
    onChange(selectedTags.filter((t) => t !== tag));
  };

  // Hide dropdown if click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        maxWidth: '640px',
        marginBottom: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {/* Input */}
      <input
        type="text"
        value={inputTag}
        placeholder={t('hashtagSelector.placeholder')}
        onChange={(e) => setInputTag(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={handleKeyDown}
        disabled={isFull}
        style={{
          padding: '10px',
          width: '90%',
          borderRadius: '20px',
          border: '1px solid #4b5563',
          backgroundColor: 'rgba(15,23,42,0.9)',
          color: '#e5e7eb',
          fontSize: 13,
        }}
      />

      {/* 🔹 Counter chip – only visible if there is at least 1 tag */}
      {selectedTags.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '90%',
          }}
        >
          <div
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 999,
              backgroundColor: 'rgba(15,23,42,1)',
              border: '1px solid rgba(148,163,184,0.6)',
              color: '#9ca3af',
              letterSpacing: '0.04em',
            }}
          >
            {selectedTags.length} / {MAX_TAGS}
          </div>
        </div>
      )}

      {/* Dropdown Suggestions */}
      {showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            background: 'rgba(15,23,42,0.98)',
            border: '1px solid rgba(148,163,184,0.4)',
            borderRadius: '14px',
            padding: '8px',
            zIndex: 100,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            maxWidth: '90%',
          }}
        >
          {suggestions.map((tag) => {
            const active = selectedTags.includes(tag);
            const disabled = isFull && !active;

            return (
              <span
                key={tag}
                onClick={() => !disabled && handleAddTag(tag)}
                style={{
                  backgroundColor: active
                    ? 'rgba(251,191,36,0.18)'
                    : 'rgba(15,23,42,1)',
                  color: active ? '#facc15' : '#e5e7eb',
                  borderRadius: '20px',
                  padding: '5px 10px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  userSelect: 'none',
                  border: active
                    ? '1px solid rgba(250,204,21,0.9)'
                    : '1px solid rgba(148,163,184,0.6)',
                  opacity: disabled ? 0.4 : 1,
                  fontSize: 11,
                }}
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}

      {/* Selected Tags */}
      <div
        style={{
          marginTop: '10px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          maxWidth: '90%',
        }}
      >
        {selectedTags.map((tag) => (
          <span
            key={tag}
            onClick={() => handleRemoveTag(tag)}
            style={{
              backgroundColor: 'rgba(15,23,42,1)',
              color: '#e5e7eb',
              padding: '6px 10px',
              borderRadius: '20px',
              cursor: 'pointer',
              userSelect: 'none',
              border: '1px solid rgba(148,163,184,0.6)',
              fontSize: 11,
            }}
          >
            {tag} ✖
          </span>
        ))}
      </div>
    </div>
  );
};

export default HashtagSelector;
